import React, { useState, useEffect } from 'react';
import { AxiosError } from 'axios';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../../app/api/api';
import { Order } from '../../../app/data/models/orders/order';
import {
  PaymentMethod,
  paymentMethodDisplayStatus,
} from '../../../app/data/models/payments/paymentMethod';
import { Payment } from '../../../app/data/models/payments/payment';

import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import {
  fetchBillerOrderDetailsAsync,
  setSelectedOrderNeedsRefresh,
} from '../billerOrdersSlice';

// Import the new component files
import PaymentSummary from './PaymentSummary';
import PaymentHistory from './PaymentHistory';
import PaymentDetailsDialog from './dialogs/PaymentDetailsDialog';
import RefundDialog from './dialogs/RefundDialog';
import PaymentDialog from './dialogs/PaymentDialog';
import ReceiptViewer, { PrinterType } from './ReceiptViewer';
import Restaurant from '@app/data/models/restaurant';

interface PaymentActionsProps {
  order: Order | null;
  restaurantId?: string;
  restaurant?: Restaurant | null;
  onOrderUpdated?: () => void;
}

const PaymentActions: React.FC<PaymentActionsProps> = ({
  order,
  restaurantId,
  restaurant,
  onOrderUpdated,
}) => {
  const dispatch = useAppDispatch();
  const { selectedOrderNeedsRefresh } = useAppSelector(
    (state) => state.billerOrders
  );
  const { t } = useTranslation();
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(PaymentMethod.Cash);
  const [amountReceived, setAmountReceived] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [printReceipt, setPrintReceipt] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null
  );
  const [processingAction, setProcessingAction] = useState(false);
  const [paymentDetailsDialogOpen, setPaymentDetailsDialogOpen] =
    useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

  const [receiptViewerOpen, setReceiptViewerOpen] = useState(false);
  const [selectedReceiptPaymentId, setSelectedReceiptPaymentId] = useState<
    string | null
  >(null);
  const [selectedReceiptRefundId, setSelectedReceiptRefundId] = useState<
    string | null
  >(null);
  const [isRefundReceipt, setIsRefundReceipt] = useState(false);
  const [isConsolidatedReceipt, setIsConsolidatedReceipt] = useState(false);
  const [printerType, setPrinterType] = useState<PrinterType>('A4');
  const [waitingForOrderUpdate, setWaitingForOrderUpdate] = useState(false);

  // Notification-driven: If selectedOrderNeedsRefresh is true, fetch latest order details
  useEffect(() => {
    const fetchOrderIfNeeded = async () => {
      if (selectedOrderNeedsRefresh && order && restaurantId) {
        setWaitingForOrderUpdate(true);
        try {
          await dispatch(
            fetchBillerOrderDetailsAsync({
              restaurantId,
              orderId: `${order.id}`,
            })
          );
          // Notify parent that order was updated
          if (onOrderUpdated) {
            onOrderUpdated();
          }
        } catch (err) {
          console.error('Error fetching order details:', err);
        } finally {
          setWaitingForOrderUpdate(false);
          dispatch(setSelectedOrderNeedsRefresh(false));
        }
      }
    };
    fetchOrderIfNeeded();
  }, [
    selectedOrderNeedsRefresh,
    order,
    restaurantId,
    dispatch,
    onOrderUpdated,
  ]);

  // Calculate total amount paid
  const totalPaid =
    order?.payments?.reduce((total, payment) => {
      return total + payment.amount;
    }, 0) || 0;

  // Calculate total amount already refunded
  const totalRefunded =
    order?.refunds?.reduce((total, refund) => {
      return total + refund.amount;
    }, 0) || 0;

  // Calculate net amount paid (paid - refunded)
  const netPaid = totalPaid - totalRefunded;

  // Calculate total order amount
  const orderTotal = order ? order.total : 0;

  // Calculate remaining amount to be paid
  const remainingAmount = Math.max(0, orderTotal - netPaid);

  // Show loading spinner while waiting for order update
  if (waitingForOrderUpdate) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!order) {
    return <></>;
  }

  // Check if order is fully paid - use the order.isPaid property directly when available
  const isFullyPaid = order?.isPaid || netPaid >= orderTotal;

  // Function to handle payment method change
  const handlePaymentMethodChange = (
    event: SelectChangeEvent<PaymentMethod>
  ) => {
    setPaymentMethod(event.target.value as PaymentMethod);
    // Clear reference number when switching to Cash
    if (event.target.value === PaymentMethod.Cash) {
      setReferenceNumber('');
    }
  };

  // Function to convert payment method enum to number for API
  const paymentMethodToNumber = (method: PaymentMethod): number => {
    switch (method) {
      case PaymentMethod.Cash:
        return 1;
      case PaymentMethod.Card:
      default:
        return 1;
    }
  };

  // Function to handle payment submission
  const handlePaymentSubmit = async () => {
    if (!order || !amountReceived) return;

    setProcessingAction(true);
    setError(null);

    try {
      const response = await api.Payments.create({
        orderId: order.id,
        restaurantId: restaurantId ? restaurantId : order.restaurantId,
        amount: parseFloat(amountReceived),
        amountReceived: parseFloat(amountReceived),
        paymentMethod: paymentMethodToNumber(paymentMethod),
        referenceNumber:
          paymentMethod !== PaymentMethod.Cash ? referenceNumber : undefined,
      });

      if (response) {
        setSuccess(t('biller.payment-success'));
        setPaymentDialogOpen(false);

        // If print receipt is checked, open receipt viewer
        if (printReceipt && response.paymentId) {
          setSelectedReceiptPaymentId(response.paymentId);
          setIsRefundReceipt(false);
          setIsConsolidatedReceipt(false);
          setReceiptViewerOpen(true);
        }

        // Trigger order refresh
        dispatch(setSelectedOrderNeedsRefresh(true));
      } else {
        setError(t('biller.payment-error'));
      }
    } catch (err: unknown) {
      console.error('Payment error:', err);
      if (err instanceof AxiosError) {
        setError(
          err.response?.data?.message ||
            err.message ||
            t('biller.payment-error')
        );
      } else if (err instanceof Error) {
        setError(err.message || t('biller.payment-error'));
      } else {
        setError(t('biller.payment-error'));
      }
    } finally {
      setProcessingAction(false);
    }
  };

  // Function to handle refund submission
  const handleRefundSubmit = async () => {
    if (!order || !selectedPaymentId || !refundAmount) return;

    setProcessingAction(true);
    setError(null);

    try {
      const response = await api.Refunds.create({
        orderId: order.id,
        restaurantId: restaurantId ? restaurantId : order.restaurantId,
        paymentId: selectedPaymentId,
        amount: parseFloat(refundAmount),
        reason: refundReason || '',
      });

      if (response) {
        setSuccess(t('biller.refund-success'));
        setRefundDialogOpen(false);

        // If print receipt is checked, open receipt viewer
        if (printReceipt && response.refundId) {
          setSelectedReceiptRefundId(response.refundId);
          setIsRefundReceipt(true);
          setIsConsolidatedReceipt(false);
          setReceiptViewerOpen(true);
        }

        // Trigger order refresh
        dispatch(setSelectedOrderNeedsRefresh(true));
      } else {
        setError(t('biller.refund-error'));
      }
    } catch (err: unknown) {
      console.error('Refund error:', err);
      if (err instanceof AxiosError) {
        setError(
          err.response?.data?.message || err.message || t('biller.refund-error')
        );
      } else if (err instanceof Error) {
        setError(err.message || t('biller.refund-error'));
      } else {
        setError(t('biller.refund-error'));
      }
    } finally {
      setProcessingAction(false);
    }
  };

  // Function to calculate change for cash payments
  const calculateChange = () => {
    if (paymentMethod !== PaymentMethod.Cash || !amountReceived) return 0;
    const receivedAmount = parseFloat(amountReceived);
    return Math.max(0, receivedAmount - remainingAmount);
  };

  // Function to handle opening the refund dialog with a specific payment
  const handleOpenRefundDialog = (paymentId: string) => {
    setSelectedPaymentId(paymentId);
    setRefundAmount('');
    setRefundReason('');
    setPrintReceipt(true);
    setRefundDialogOpen(true);

    // Find the payment to get its amount
    const payment = order?.payments?.find((p) => p.id === paymentId);
    if (payment) {
      // Calculate already refunded amount for this payment
      const alreadyRefunded =
        order?.refunds
          ?.filter((r) => r.paymentId === paymentId)
          .reduce((total, refund) => total + refund.amount, 0) || 0;
      // Set default refund amount to remaining refundable amount
      setRefundAmount((payment.amount - alreadyRefunded).toFixed(2));
    }
  };

  // Function to get payment method display text
  const getPaymentMethodDisplay = (method: PaymentMethod): string => {
    return paymentMethodDisplayStatus(method) || method.toString();
  };

  // Function to print a specific payment receipt
  const handlePrintPaymentReceipt = (paymentId: string) => {
    setSelectedReceiptPaymentId(paymentId);
    setSelectedReceiptRefundId(null);
    setIsRefundReceipt(false);
    setIsConsolidatedReceipt(false);
    setReceiptViewerOpen(true);
  };

  // Function to print a specific refund receipt
  const handlePrintRefundReceipt = (refundId: string) => {
    setSelectedReceiptRefundId(refundId);
    setSelectedReceiptPaymentId(null);
    setIsRefundReceipt(true);
    setIsConsolidatedReceipt(false);
    setReceiptViewerOpen(true);
  };

  // Function to print consolidated receipt
  const handlePrintConsolidatedReceipt = () => {
    setSelectedReceiptPaymentId(null);
    setSelectedReceiptRefundId(null);
    setIsRefundReceipt(false);
    setIsConsolidatedReceipt(true);
    setReceiptViewerOpen(true);
  };

  // Function to view payment details
  const handleViewPaymentDetails = (payment: Payment) => {
    setSelectedPayment(payment);
    setPaymentDetailsDialogOpen(true);
  };

  // Handle payment dialog open - set default received amount
  const handleOpenPaymentDialog = () => {
    setPaymentMethod(PaymentMethod.Cash);
    setAmountReceived(remainingAmount.toFixed(2));
    setReferenceNumber('');
    setPrintReceipt(true);
    setPaymentDialogOpen(true);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Snackbar for error/success feedback */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      {/* Printer Type Selector */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel id="printer-type-label">
                {t('biller.printer-type')}
              </InputLabel>
              <Select
                labelId="printer-type-label"
                value={printerType}
                label={t('biller.printer-type')}
                onChange={(e) => setPrinterType(e.target.value as PrinterType)}
              >
                <MenuItem value="Thermal">Thermal</MenuItem>
                <MenuItem value="A4">A4</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Grid>

        {/* Payment Summary Section */}
        <PaymentSummary
          order={order}
          restaurant={restaurant}
          totalPaid={totalPaid}
          totalRefunded={totalRefunded}
          netPaid={netPaid}
          remainingAmount={remainingAmount}
          isFullyPaid={isFullyPaid}
          onOpenPaymentDialog={handleOpenPaymentDialog}
          onPrintConsolidatedReceipt={handlePrintConsolidatedReceipt}
        />

        {/* Payment History Section */}
        <PaymentHistory
          order={order}
          onViewPaymentDetails={handleViewPaymentDetails}
          onPrintPaymentReceipt={handlePrintPaymentReceipt}
          onPrintRefundReceipt={handlePrintRefundReceipt}
          getPaymentMethodDisplay={getPaymentMethodDisplay}
        />
      </Grid>

      {/* Payment Dialog */}
      <PaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        paymentMethod={paymentMethod}
        amountReceived={amountReceived}
        referenceNumber={referenceNumber}
        printReceipt={printReceipt}
        processingAction={processingAction}
        remainingAmount={remainingAmount}
        onPaymentMethodChange={handlePaymentMethodChange}
        onAmountReceivedChange={(value) => setAmountReceived(value)}
        onReferenceNumberChange={(value) => setReferenceNumber(value)}
        onPrintReceiptChange={(checked) => setPrintReceipt(checked)}
        onPaymentSubmit={handlePaymentSubmit}
        calculateChange={calculateChange}
        getPaymentMethodDisplay={getPaymentMethodDisplay}
      />

      {/* Refund Dialog */}
      <RefundDialog
        open={refundDialogOpen}
        onClose={() => setRefundDialogOpen(false)}
        selectedPayment={
          selectedPaymentId
            ? order.payments?.find((p) => p.id === selectedPaymentId) || null
            : null
        }
        refundAmount={refundAmount}
        refundReason={refundReason}
        printReceipt={printReceipt}
        processingAction={processingAction}
        onRefundAmountChange={(value) => setRefundAmount(value)}
        onRefundReasonChange={(value) => setRefundReason(value)}
        onPrintReceiptChange={(checked) => setPrintReceipt(checked)}
        onRefundSubmit={handleRefundSubmit}
      />

      {/* Payment Details Dialog */}
      <PaymentDetailsDialog
        open={paymentDetailsDialogOpen}
        onClose={() => setPaymentDetailsDialogOpen(false)}
        selectedPayment={selectedPayment}
        order={order}
        processingAction={processingAction}
        onPrintReceipt={handlePrintPaymentReceipt}
        onOpenRefundDialog={(paymentId) => {
          handleOpenRefundDialog(paymentId);
          setPaymentDetailsDialogOpen(false);
        }}
        getPaymentMethodDisplay={getPaymentMethodDisplay}
      />

      {/* Receipt Viewer */}
      {order && (
        <ReceiptViewer
          open={receiptViewerOpen}
          // Prevent pop-up from closing on OrderDetails reload
          onClose={() => {
            setReceiptViewerOpen(false);
            setIsConsolidatedReceipt(false);
            setSelectedReceiptPaymentId(null);
            setSelectedReceiptRefundId(null);
            setIsRefundReceipt(false);
          }}
          orderId={order.id}
          paymentId={selectedReceiptPaymentId || undefined}
          refundId={selectedReceiptRefundId || undefined}
          isRefund={isRefundReceipt}
          isConsolidated={isConsolidatedReceipt}
          printerType={printerType}
          key={`receipt-${order.id}-${selectedReceiptPaymentId || ''}-${selectedReceiptRefundId || ''}-${isRefundReceipt}-${isConsolidatedReceipt}`}
        />
      )}
    </Box>
  );
};

export default PaymentActions;
